import { superjsonCodec } from './superjsonCodec.js';

/**
 * Parses a JSON string or returns the input directly if it is already an object.
 * Supports decoding of JavaScript types when specified and can provide fallback
 * behavior in case of parsing errors.
 *
 * @param {string|Object} str - The JSON string to parse or an object to return as-is.
 * @param {Object} [options] - Options for parsing.
 * @param {boolean} [options.decodeJSTypes=false] - If `true`, uses a custom parser to decode
 *   complex JavaScript types instead of standard JSON parsing.
 * @param {boolean} [options.fallback=false] - If `true`, returns the original input string
 *   if parsing fails, instead of returning `undefined`.
 *
 * @returns {Object|undefined} The parsed object if successful, the original string if fallback is
 *   enabled and parsing fails, or `undefined` if parsing fails without fallback.
 *
 * @example
 * // Parsing a valid JSON string
 * const jsonString = '{"name":"Alice","age":30}';
 * const result = parseJson(jsonString);
 * console.log(result); // Output: { name: 'Alice', age: 30 }
 *
 * @example
 * // Returning an object directly
 * const obj = { name: 'Bob', age: 25 };
 * const result = parseJson(obj);
 * console.log(result); // Output: { name: 'Bob', age: 25 }
 *
 * @example
 * // Handling parsing error with fallback
 * const invalidJson = '{"name":"Alice",age:30}'; // Invalid JSON (missing quotes around "age")
 * const result = parseJson(invalidJson, { fallback: true });
 * console.log(result); // Output: '{"name":"Alice",age:30}'
 *
 * @example
 * // Using custom type decoding with superjson
 * const customJsonString = '{"json":{"date":"2024-10-14T12:34:56.000Z"},"meta":{"values":{"date":["Date"]}}}';
 * const result = parseJson(customJsonString, { decodeJSTypes: true });
 * console.log(result); // Output: { date: new Date('2024-10-14T12:34:56.000Z') }
 */
export const parseJson = (str, { decodeJSTypes = false, ...opts } = {}) => {
  if (typeof str === 'object') {
    return str;
  }

  try {
    if (decodeJSTypes === true) {
      return superjsonCodec.parse(str);
    }

    return JSON.parse(str);
  } catch (error) {
    if (opts?.fallback === true) {
      return str;
    }
    return undefined;
  }
};
